import React from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import { 
  Google as GoogleIcon, 
  Security as SecurityIcon, 
  People as PeopleIcon, 
  BarChart as BarChartIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const { login } = useAuth();

  return (
    <Container 
      component="main" 
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 }
      }}
    >
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 400 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
          <Avatar 
            sx={{ 
              bgcolor: 'primary.main', 
              width: { xs: 56, sm: 64 }, 
              height: { xs: 56, sm: 64 }, 
              mx: 'auto', 
              mb: 2 
            }}
          >
            <SecurityIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
          </Avatar>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Analizez.eu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
            Analizează produsele alimentare pentru sănătatea ta
          </Typography>
        </Box>

        {/* Login Card */}
        <Paper elevation={8} sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Conectează-te la contul tău
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              Folosește contul tău Google pentru a accesa platforma
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={login}
            sx={{ 
              mb: 3,
              py: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}
          >
            Continuă cu Google
          </Button>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Funcționalități:
          </Typography>
          <List dense>
            <ListItem sx={{ py: { xs: 0.5, sm: 1 } }}>
              <ListItemIcon>
                <SecurityIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </ListItemIcon>
              <ListItemText 
                primary="Analiză completă a produselor alimentare"
                primaryTypographyProps={{ 
                  variant: 'body2',
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
            </ListItem>
            <ListItem sx={{ py: { xs: 0.5, sm: 1 } }}>
              <ListItemIcon>
                <BarChartIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </ListItemIcon>
              <ListItemText 
                primary="Recomandări de sănătate personalizate"
                primaryTypographyProps={{ 
                  variant: 'body2',
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
            </ListItem>
            <ListItem sx={{ py: { xs: 0.5, sm: 1 } }}>
              <ListItemIcon>
                <PeopleIcon color="primary" sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </ListItemIcon>
              <ListItemText 
                primary="Istoricul scanărilor și progresul personal"
                primaryTypographyProps={{ 
                  variant: 'body2',
                  sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                }}
              />
            </ListItem>
          </List>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            Doar utilizatorii autorizați pot accesa această platformă.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
            Contactează un administrator dacă ai nevoie de acces.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
